type Msg = { id: string; role: string; content: string };

type PublicState = {
  id: string;
  handoffStatus: "bot" | "human";
  messages: Msg[];
};

const WIDGET_BUILD = "20260906f";

function currentScript(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement)
    return document.currentScript;
  const scripts = document.querySelectorAll("script[data-chatbot-key]");
  return (scripts[scripts.length - 1] as HTMLScriptElement) ?? null;
}

function apiBaseFromScript(script: HTMLScriptElement): string {
  const override = script.dataset.apiBase;
  if (override) return override.replace(/\/$/, "");
  try {
    const src = new URL(script.src, window.location.href);
    return src.origin;
  } catch {
    return window.location.origin;
  }
}

function storageKey(chatbotKey: string) {
  return `fs-chatbot:${chatbotKey}:conversation`;
}

function escapeHtml(text: string) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function stripCitations(text: string) {
  return text
    .replace(/\\n/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/[\u200e\u200f\u202a-\u202e]/g, "")
    .replace(/[＊∗﹡✱]/g, "*")
    .replace(/\s*\[\d+\]/g, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function formatInline(escaped: string) {
  return escaped
    .replace(/\*\*([\s\S]+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>");
}

function markdownToHtml(raw: string) {
  const text = stripCitations(raw);
  if (!text) return "";
  const out: string[] = [];
  let para: string[] = [];

  const flushPara = () => {
    if (!para.length) return;
    out.push(`<p>${para.join("<br>")}</p>`);
    para = [];
  };

  for (const line of text.split("\n")) {
    const heading = line.match(/^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (heading) {
      flushPara();
      const level = Math.min(heading[1]!.length, 6);
      out.push(
        `<h${level}>${formatInline(escapeHtml(heading[2]!.trim()))}</h${level}>`,
      );
      continue;
    }
    if (!line.trim()) {
      flushPara();
      continue;
    }
    const normalized = line.replace(/^\s*[*+-]\s+/, "• ");
    para.push(formatInline(escapeHtml(normalized)));
  }
  flushPara();
  return out.join("");
}

function renderMarkdownInto(container: HTMLElement, raw: string) {
  const root = el("div", { class: "md" });
  root.innerHTML = markdownToHtml(raw);
  container.replaceChildren(root);
}

const CSS = `
:host { all: initial; }
.wrap {
  font-family: Tahoma, ui-sans-serif, system-ui, sans-serif;
  font-size: 14px; color: #0f172a;
  direction: rtl;
}
.launcher {
  position: fixed; right: 20px; bottom: 20px; z-index: 2147483000;
  width: 56px; height: 56px; border-radius: 999px; border: 0; cursor: pointer;
  background: var(--accent, #2563eb); color: #fff; box-shadow: 0 8px 24px rgba(15,23,42,.25);
  display: grid; place-items: center;
}
.launcher svg, .head button svg { display: block; }
.panel {
  position: fixed; right: 20px; bottom: 88px; z-index: 2147483000;
  width: min(380px, calc(100vw - 24px)); height: min(560px, calc(100vh - 120px));
  background: #fff; border-radius: 16px; box-shadow: 0 16px 50px rgba(15,23,42,.22);
  display: none; flex-direction: column; overflow: hidden; border: 1px solid #e2e8f0;
  direction: rtl;
}
.panel.open { display: flex; }
.head { padding: 12px 14px; background: var(--accent, #2563eb); color: #fff; display: flex; justify-content: space-between; align-items: center; flex-shrink: 0; }
.head h1 { margin: 0; font-size: 15px; font-weight: 600; unicode-bidi: plaintext; text-align: start; }
.head button { background: transparent; border: 0; color: #fff; cursor: pointer; display: grid; place-items: center; padding: 2px; }
.banner { background: #fef3c7; color: #92400e; padding: 8px 12px; font-size: 12px; text-align: start; flex-shrink: 0; }
.msgs {
  flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 12px;
  display: flex; flex-direction: column; gap: 8px; background: #f8fafc;
  direction: ltr; overscroll-behavior: contain;
}
.bubble {
  max-width: 85%; padding: 8px 10px; border-radius: 12px; line-height: 1.55;
  text-align: start;
}
.bubble.user { align-self: flex-end; background: var(--accent, #2563eb); color: #fff; white-space: pre-wrap; unicode-bidi: plaintext; }
.bubble.assistant { align-self: flex-start; background: #fff; border: 1px solid #e2e8f0; }
.bubble.human_agent { align-self: flex-start; background: #ecfdf5; border: 1px solid #a7f3d0; }
.label {
  display: block; font-size: 11px; opacity: .7; margin-bottom: 4px;
  direction: rtl; unicode-bidi: isolate; text-align: right;
}
.md { direction: rtl; text-align: start; display: block; }
.md p { display: block; margin: 0 0 0.55em; }
.md p:last-child { margin-bottom: 0; }
.md h1, .md h2, .md h3, .md h4, .md h5, .md h6 {
  display: block; font-weight: 700; line-height: 1.4;
  margin: 0.7em 0 0.35em;
}
.md h1 { font-size: 1.16em; }
.md h2 { font-size: 1.1em; }
.md h3, .md h4, .md h5, .md h6 { font-size: 1.04em; }
.md h1:first-child, .md h2:first-child, .md h3:first-child,
.md h4:first-child, .md h5:first-child, .md h6:first-child { margin-top: 0; }
.md ol, .md ul { display: block; margin: 0.35em 0 0.55em; padding-inline-start: 1.4em; }
.md ol { list-style-type: decimal; }
.md ul { list-style-type: disc; }
.md li { display: list-item; margin: 0.25em 0; }
.md li ul { list-style-type: circle; margin: 0.25em 0 0.1em; }
.md strong { font-weight: 700; }
.bubble.loading {
  align-self: flex-start; background: #e2e8f0; border: 1px solid #cbd5e1; min-width: 170px;
}
.loading-row { display: flex; align-items: center; gap: 8px; direction: rtl; min-height: 24px; }
.loading-text { font-size: 13px; color: #334155; }
.dots { display: flex; gap: 5px; direction: ltr; align-items: center; }
.dots span {
  display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: #64748b;
  animation: fs-dot 1.2s infinite ease-in-out;
}
.dots span:nth-child(2) { animation-delay: .15s; }
.dots span:nth-child(3) { animation-delay: .3s; }
@keyframes fs-dot {
  0%, 80%, 100% { opacity: .35; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
}
.form { display: flex; gap: 8px; padding: 10px; border-top: 1px solid #e2e8f0; background: #fff; direction: rtl; flex-shrink: 0; }
.form input {
  flex: 1; border: 1px solid #cbd5e1; border-radius: 10px; padding: 8px 10px;
  unicode-bidi: plaintext; text-align: start;
}
.form button { background: var(--accent, #2563eb); color: #fff; border: 0; border-radius: 10px; padding: 8px 12px; cursor: pointer; }
.form button:disabled, .form input:disabled { opacity: .6; cursor: default; }
`;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  text?: string,
) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") node.className = v;
    else node.setAttribute(k, v);
  }
  if (text) node.textContent = text;
  return node;
}

function svgIcon(pathD: string, size: number) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", pathD);
  svg.append(path);
  return svg;
}

function makeLoadingBubble() {
  const wrapB = el("div", { class: "bubble assistant loading", dir: "rtl" });
  wrapB.dataset.role = "assistant";
  wrapB.dataset.content = "";
  wrapB.append(el("div", { class: "label", dir: "rtl" }, "دستیار"));
  const row = el("div", { class: "loading-row" });
  const dots = el("div", { class: "dots" });
  dots.setAttribute("aria-hidden", "true");
  dots.append(el("span"), el("span"), el("span"));
  row.append(el("span", { class: "loading-text" }, "در حال پاسخگویی"));
  row.append(dots);
  wrapB.append(row);
  return wrapB;
}

async function boot() {
  const script = currentScript();
  if (!script) return;
  const chatbotKey = script.dataset.chatbotKey;
  if (!chatbotKey) return;
  const origin = apiBaseFromScript(script);
  const key = chatbotKey;

  const host = el("div");
  host.dataset.widgetBuild = WIDGET_BUILD;
  const shadow = host.attachShadow({ mode: "open" });
  const style = el("style");
  style.textContent = CSS;
  const wrap = el("div", { class: "wrap", lang: "fa", dir: "rtl" });
  const launcher = el("button", {
    class: "launcher",
    type: "button",
    "aria-label": "Open chat",
  });
  launcher.append(
    svgIcon(
      "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
      26,
    ),
  );
  const panel = el("div", { class: "panel" });
  const head = el("div", { class: "head" });
  const title = el("h1", { dir: "auto" }, "Chat");
  const close = el("button", { type: "button", "aria-label": "Close chat" });
  close.append(svgIcon("M18 6 6 18M6 6l12 12", 18));
  head.append(title, close);
  const banner = el(
    "div",
    { class: "banner" },
    "یکی از اعضای تیم به این گفتگو پیوسته است.",
  );
  banner.style.display = "none";
  const msgs = el("div", { class: "msgs" });
  const form = el("form", { class: "form" });
  const input = el("input", {
    type: "text",
    placeholder: "سوالی بپرسید...",
    autocomplete: "off",
    dir: "rtl",
  }) as HTMLInputElement;
  const send = el("button", { type: "submit" }, "ارسال");
  form.append(input, send);
  panel.append(head, banner, msgs, form);
  wrap.append(launcher, panel);
  shadow.append(style, wrap);
  document.body.append(host);

  let accent = "#2563eb";
  let welcomeMessage = "سلام — سوالی دارید؟";
  try {
    const cfg = await fetch(
      `${origin}/api/public/config/${encodeURIComponent(key)}`,
    ).then((r) => r.json());
    title.textContent = cfg.name || "Chat";
    accent = cfg.accentColor || accent;
    wrap.style.setProperty("--accent", accent);
    if (typeof cfg.welcomeMessage === "string") {
      welcomeMessage = cfg.welcomeMessage;
    }
  } catch {
    /* use defaults */
  }

  let conversationId = localStorage.getItem(storageKey(key));
  let handoff: "bot" | "human" = "bot";
  let pollTimer: number | undefined;
  let sending = false;
  let refreshEpoch = 0;
  let readingOlder = false;
  let ignoreScroll = false;
  let lastScrollTop = 0;
  let renderedKey = "";

  function nearBottom() {
    return msgs.scrollHeight - msgs.scrollTop - msgs.clientHeight < 48;
  }

  function scrollToBottom() {
    ignoreScroll = true;
    msgs.scrollTop = msgs.scrollHeight;
    requestAnimationFrame(() => {
      msgs.scrollTop = msgs.scrollHeight;
      lastScrollTop = msgs.scrollTop;
      ignoreScroll = false;
    });
  }

  function scrollIfPinned(force = false) {
    if (force) {
      readingOlder = false;
      scrollToBottom();
      return;
    }
    if (!readingOlder) scrollToBottom();
  }

  msgs.addEventListener(
    "scroll",
    () => {
      if (ignoreScroll) return;
      if (msgs.scrollTop + 2 < lastScrollTop) readingOlder = true;
      if (nearBottom()) readingOlder = false;
      lastScrollTop = msgs.scrollTop;
    },
    { passive: true },
  );

  function rememberConversation(id: string | null | undefined) {
    if (!id) return;
    conversationId = id;
    localStorage.setItem(storageKey(key), id);
  }

  function appendBubble(m: Msg) {
    const wrapB = el("div", { class: `bubble ${m.role}`, dir: "auto" });
    wrapB.dataset.content = m.content;
    wrapB.dataset.role = m.role;
    if (m.role === "human_agent") {
      wrapB.append(el("div", { class: "label", dir: "rtl" }, "پشتیبانی"));
    } else if (m.role === "assistant") {
      wrapB.append(el("div", { class: "label", dir: "rtl" }, "دستیار"));
    }
    const body = el("div", { class: "body" });
    if (m.role === "user") {
      body.append(document.createTextNode(m.content));
    } else {
      renderMarkdownInto(body, m.content);
    }
    wrapB.append(body);
    msgs.append(wrapB);
    return wrapB;
  }

  function renderWelcome() {
    msgs.replaceChildren();
    renderedKey = "";
    if (!welcomeMessage.trim()) return;
    appendBubble({ id: "welcome", role: "assistant", content: welcomeMessage });
  }

  function messagesKey(list: Msg[]) {
    return `${handoff}|${list.map((m) => `${m.id}:${m.role}:${m.content}`).join("\n")}`;
  }

  function readDomMessages(): Msg[] {
    return Array.from(msgs.querySelectorAll(".bubble"))
      .filter((node) => !node.classList.contains("loading"))
      .map((node) => ({
        id: crypto.randomUUID(),
        role:
          (node as HTMLElement).dataset.role ||
          (node.classList.contains("user")
            ? "user"
            : node.classList.contains("human_agent")
              ? "human_agent"
              : "assistant"),
        content: (node as HTMLElement).dataset.content ?? "",
      }));
  }

  function renderMessages(list: Msg[], forceBottom = false) {
    if (list.length === 0) {
      renderWelcome();
      return;
    }
    const nextKey = messagesKey(list);
    if (nextKey === renderedKey) return;
    renderedKey = nextKey;
    const pinned = forceBottom || !readingOlder;
    const prevTop = msgs.scrollTop;
    msgs.replaceChildren();
    for (const m of list) appendBubble(m);
    if (pinned) {
      readingOlder = false;
      scrollToBottom();
    } else {
      ignoreScroll = true;
      msgs.scrollTop = prevTop;
      lastScrollTop = prevTop;
      requestAnimationFrame(() => {
        ignoreScroll = false;
      });
    }
  }

  function setHandoff(next: "bot" | "human") {
    handoff = next;
    banner.style.display = next === "human" ? "block" : "none";
    if (next === "human") startPoll();
    else stopPoll();
  }

  function setBusy(busy: boolean) {
    sending = busy;
    input.disabled = busy;
    send.disabled = busy;
    if (busy) {
      refreshEpoch += 1;
      stopPoll();
    }
  }

  async function refresh() {
    if (!conversationId || sending) return;
    const epoch = refreshEpoch;
    const res = await fetch(
      `${origin}/api/public/conversations/${conversationId}?chatbotKey=${encodeURIComponent(key)}`,
    );
    if (sending || epoch !== refreshEpoch) return;
    if (!res.ok) return;
    const data = (await res.json()) as PublicState;
    if (sending || epoch !== refreshEpoch) return;
    setHandoff(data.handoffStatus);
    renderMessages(data.messages);
  }

  function startPoll() {
    if (!conversationId || pollTimer || handoff !== "human") return;
    void refresh();
    pollTimer = window.setInterval(() => {
      refresh().catch(() => undefined);
    }, 2000);
  }
  function stopPoll() {
    if (pollTimer) {
      window.clearInterval(pollTimer);
      pollTimer = undefined;
    }
  }

  if (conversationId) {
    await refresh().catch(() => {
      conversationId = null;
      localStorage.removeItem(storageKey(key));
    });
  }
  if (!conversationId) {
    renderWelcome();
  }

  launcher.addEventListener("click", () => {
    panel.classList.add("open");
    readingOlder = false;
    if (handoff === "human") startPoll();
    else void refresh();
    input.focus();
    scrollIfPinned(true);
  });
  close.addEventListener("click", () => {
    panel.classList.remove("open");
    stopPoll();
  });

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text || sending) return;
    input.value = "";
    setBusy(true);
    readingOlder = false;
    const optimistic = [
      ...readDomMessages(),
      { id: "tmp-user", role: "user", content: text },
    ];
    renderMessages(optimistic, true);

    const assistantNode = makeLoadingBubble();
    msgs.append(assistantNode);
    scrollIfPinned(true);

    let streamed = "";
    const body = el("div", { class: "body" });
    const showStream = () => {
      if (assistantNode.classList.contains("loading")) {
        assistantNode.classList.remove("loading");
        assistantNode.replaceChildren(
          el("div", { class: "label", dir: "rtl" }, "دستیار"),
          body,
        );
      }
      assistantNode.dataset.content = streamed;
      renderMarkdownInto(body, streamed);
      scrollIfPinned();
    };

    try {
      const res = await fetch(`${origin}/api/public/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chatbotKey: key,
          message: text,
          ...(conversationId ? { conversationId } : {}),
        }),
      });

      if (res.headers.get("content-type")?.includes("application/json")) {
        const data = await res.json();
        rememberConversation(data.conversationId);
        if (data.handoffStatus === "human") {
          assistantNode.remove();
          setHandoff("human");
        }
        return;
      }

      rememberConversation(res.headers.get("X-Conversation-Id"));

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) return;
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";
        for (const event of events) {
          for (const line of event.split("\n")) {
            if (!line.startsWith("data:")) continue;
            const payload = line.slice(5).trim();
            if (!payload || payload === "[DONE]") continue;
            try {
              const json = JSON.parse(payload) as {
                type?: string;
                delta?: string;
                data?: {
                  conversationId?: string;
                  handoffStatus?: "bot" | "human";
                };
              };
              if (json.data?.conversationId)
                rememberConversation(json.data.conversationId);
              if (json.data?.handoffStatus) setHandoff(json.data.handoffStatus);
              const delta =
                json.type === "text-delta" && json.delta
                  ? json.delta
                  : typeof json.delta === "string"
                    ? json.delta
                    : "";
              if (delta) {
                streamed += delta;
                showStream();
              }
            } catch {
              streamed += payload;
              showStream();
            }
          }
        }
      }
    } finally {
      if (assistantNode.classList.contains("loading")) assistantNode.remove();
      setBusy(false);
      renderedKey = "";
      await refresh().catch(() => undefined);
      if (handoff === "human") startPoll();
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => void boot());
} else {
  void boot();
}
